// ─── MCA Filing Index Section — extracted from AdminCCFSLeads.jsx (Phase 2c) ───
// Largest section: owns upload, FY-verification table, STK-1 notice toggle, filing search/list,
// and the gap-rules-driven row reclassification logic.
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  FileCheck, AlertCircle, ChevronUp, ChevronDown, FolderOpen,
  Download, Lock, Upload, Loader2, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtDatePretty } from '@/pages/admin/ccfs/utils';

const API = process.env.REACT_APP_BACKEND_URL;

export function FilingIndexSection({ lead, onRefresh, readOnly = false, sectionKey, activeSection, setActiveSection, apiBase = '/ccfs-leads' }) {
  const fi = lead.filing_index || {};
  const filings = fi.filings || [];
  const controlled = !!sectionKey && !!setActiveSection;
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const expanded = controlled ? activeSection === sectionKey : uncontrolledExpanded;
  const setExpanded = (v) => {
    if (controlled) setActiveSection(v ? sectionKey : null);
    else setUncontrolledExpanded(v);
  };
  const [searchQ, setSearchQ] = useState('');
  const [yearFilter, setYearFilter] = useState('ALL');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingNotice, setSavingNotice] = useState(false);
  const fileRef = useRef(null);

  // Derive BS FY from MCA Master Data date_of_balance_sheet
  const bsFY = (() => {
    const bsDate = lead.mca_company_info?.date_of_balance_sheet || '';
    if (!bsDate) return null;
    let yr, mn;
    if (bsDate.includes('/')) {
      const p = bsDate.split('/');
      yr = parseInt(p[2]); mn = parseInt(p[1]);
    } else {
      yr = parseInt(bsDate.slice(0,4)); mn = parseInt(bsDate.slice(5,7));
    }
    if (!yr) return null;
    return mn <= 3 ? yr - 1 : yr; // FY start year (31/03/2025 → FY 2024-25 → 2024)
  })();

  const incorpYear = (() => {
    // Indian FY = Apr → Mar. If incorporated in Jan-Mar, FY started previous April.
    const mca_d = lead.mca_company_info?.date_of_incorporation || '';
    const parseFYStart = (yr, mn) => (mn >= 4 ? yr : yr - 1);
    if (mca_d) {
      let yr, mn;
      // Accept ISO (YYYY-MM-DD) or DD/MM/YYYY or DD-MM-YYYY
      const iso = mca_d.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (iso) { yr = parseInt(iso[1]); mn = parseInt(iso[2]); }
      else {
        const dmy = mca_d.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
        if (dmy) { yr = parseInt(dmy[3]); mn = parseInt(dmy[2]); }
      }
      if (yr && mn) return parseFYStart(yr, mn);
    }
    // Fallback: SPiCe form date in filings — extract year + month
    const spice = filings.find(f => (f.form_name||'').toUpperCase().includes('SPICE'));
    if (spice?.date_of_filing) {
      const yr = parseInt(spice.date_of_filing.slice(0,4));
      const mn = parseInt(spice.date_of_filing.slice(5,7));
      if (yr && mn) return parseFYStart(yr, mn);
    }
    return null;
  })();

  // Build Other Key Filings map: FY → filings (all ADT-*, PAS-3, SH-4, SH-7, MGT-14, PAS-6, INC-20A, DIR-12, INC-21)
  const OTHER_KEY_PATTERNS = [
    { match: /ADT[\s-]*(\d+)/i, getLabel: (fn) => { const m = fn.match(/ADT[\s-]*(\d+)/i); return `ADT-${m?.[1] || '?'}`; } },
    { match: /PAS[\s-]*3/i, label: 'PAS-3' },
    { match: /SH[\s-]*4/i, label: 'SH-4' },
    { match: /SH[\s-]*7/i, label: 'SH-7' },
    { match: /MGT[\s-]*14/i, label: 'MGT-14' },
    { match: /PAS[\s-]*6/i, label: 'PAS-6' },
    { match: /INC[\s-]*20A/i, label: 'INC-20A' },
    { match: /DIR[\s-]*12/i, label: 'DIR-12' },
    { match: /INC[\s-]*21/i, label: 'INC-21' },
  ];
  const otherKeyByFY = {};
  filings.filter(f => f.date_of_filing).forEach(f => {
    const fn = f.form_name || '';
    for (const p of OTHER_KEY_PATTERNS) {
      if (p.match.test(fn)) {
        const yr = parseInt(f.date_of_filing.slice(0,4)), mn = parseInt(f.date_of_filing.slice(5,7));
        const fyStart = mn >= 4 ? yr : yr - 1;
        const fyLabel = `${fyStart}-${String(fyStart+1).slice(-2)}`;
        if (!otherKeyByFY[fyLabel]) otherKeyByFY[fyLabel] = [];
        const label = typeof p.getLabel === 'function' ? p.getLabel(fn) : p.label;
        // Deduplicate: same form + same date = 1 entry
        const key = `${label}|${f.date_of_filing}`;
        if (!otherKeyByFY[fyLabel].some(e => `${e.form}|${e.date}` === key)) {
          otherKeyByFY[fyLabel].push({ form: label, date: f.date_of_filing });
        }
        break;
      }
    }
  });
  // Legacy compat: keep adt1ByFY for backwards references
  const adt1ByFY = {};
  filings.filter(f => (f.form_name||'').toUpperCase().replace(/\s+/g,'').includes('ADT-1') && f.date_of_filing).forEach(f => {
    const yr = parseInt(f.date_of_filing.slice(0,4)), mn = parseInt(f.date_of_filing.slice(5,7));
    const fyStart = mn >= 4 ? yr : yr - 1;
    const fyLabel = `${fyStart}-${String(fyStart+1).slice(-2)}`;
    if (!adt1ByFY[fyLabel]) adt1ByFY[fyLabel] = new Set();
    adt1ByFY[fyLabel].add(f.date_of_filing);
  });
  Object.keys(adt1ByFY).forEach(k => { adt1ByFY[k] = [...adt1ByFY[k]]; });

  // Initialize FY verification rows from saved state or defaults
  const savedRows = fi.fy_verification?.rows || [];
  const buildDefaultRows = () => {
    if (!incorpYear) return [];
    // F4/F6: derive current FY dynamically from today (India FY: Apr → Mar)
    const _today = new Date();
    const _tYear = _today.getFullYear();
    const _tMonth = _today.getMonth() + 1; // 1-12
    const currentFY = _tMonth <= 3 ? _tYear - 1 : _tYear; // start year of current FY

    // Gap rule context from filing index
    const gapMeta = fi.gap_rules_meta || {};
    const cessationFYStr = gapMeta.cessation_fy || lead.cessation_fy_override || lead.cessation_fy || '';
    let cessationFYYear = null;
    if (cessationFYStr) {
      // Strip "FY " prefix; extract first 4-digit year (handles "FY 2023-24", "2023-24", "FY2023-24")
      const m = cessationFYStr.toString().match(/(\d{4})/);
      if (m) {
        const y = parseInt(m[1], 10);
        if (!isNaN(y)) cessationFYYear = y;
      }
    }
    // Resolve effective intent in priority order:
    //   lead.intent_override (admin Engagement Override) → gap_rules_meta.intent
    //   → quiz_answers[2] (user's original calc answer) → lead.intent
    // Mirrors the backend recalc_savings precedence so chip + table never disagree.
    const effectiveIntent = (lead.intent_override || gapMeta.intent || lead.quiz_answers?.[2] || lead.intent || '').toString().toLowerCase().trim();
    // Section 8 (NPL) companies cannot strike off — they MUST regularise all filings till
    // current FY regardless of intent or cessation. Override the strike-off classification.
    const isSection8 = !!lead?.cin_info?.is_section_8;
    // Strike-off / close = post-cessation filings are not required. Default = close when intent unknown.
    // For Section 8: never treat as strike-off — all FYs remain mandatory.
    const isStrikeOff = !isSection8 && (!effectiveIntent || effectiveIntent === 'strike_off' || effectiveIntent === 'close');
    const noticeReceived = gapMeta.notice_248_status === 'yes';

    // F4: date-based "Not Yet Due" computation
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth() + 1; // 1-12
    // AOC-4 due ~30 Oct of (FY+1), MGT-7 due ~29 Nov of (FY+1)
    const aoc4NotYetDue = (fy) => {
      const dueYear = fy + 1;
      if (todayYear < dueYear) return true;
      if (todayYear === dueYear && todayMonth < 11) return true;
      return false;
    };
    const mgt7NotYetDue = (fy) => {
      const dueYear = fy + 1;
      if (todayYear < dueYear) return true;
      if (todayYear === dueYear && todayMonth < 12) return true;
      return false;
    };

    const rows = [];
    for (let fy = incorpYear; fy <= currentFY; fy++) {
      const fyLabel = `${fy}-${String(fy+1).slice(-2)}`;
      const saved = savedRows.find(r => r.fy === fyLabel);
      if (saved) {
        // Apply gap rule to saved rows too
        if (isStrikeOff && !noticeReceived && cessationFYYear && fy > cessationFYYear) {
          saved.not_required = true;
        } else if (saved.not_required) {
          // Iter 89: clear stale `not_required:true` saved during a prior strike-off
          // attempt when current intent no longer implies strike-off (e.g. admin
          // flipped to 'continue' / 'regularise' OR company is Section 8). Restores
          // the FY to its real status so it's counted as a gap again.
          saved.not_required = false;
          const isBS_FY = bsFY === fy;
          const isBeforeBS = bsFY ? fy < bsFY : false;
          if (saved.aoc4 === 'not_required') {
            saved.aoc4 = isBS_FY || isBeforeBS ? 'filed' : (aoc4NotYetDue(fy) ? 'not_yet_due' : 'not_filed');
          }
          if (saved.mgt7 === 'not_required') {
            saved.mgt7 = isBS_FY || isBeforeBS ? 'filed' : (mgt7NotYetDue(fy) ? 'not_yet_due' : 'not_filed');
          }
        }
        rows.push(saved);
        continue;
      }
      const isBS_FY = bsFY === fy;
      const isBeforeBS = bsFY ? fy < bsFY : false;
      const aocNotDue = aoc4NotYetDue(fy);
      const mgtNotDue = mgt7NotYetDue(fy);

      // Gap rule: post-cessation FYs are not required for strike-off (unless notice received)
      const isNotRequired = isStrikeOff && !noticeReceived && cessationFYYear && fy > cessationFYYear;

      const aoc4Status = isBS_FY ? 'filed'
        : isBeforeBS ? 'filed'
        : isNotRequired ? 'not_required'
        : aocNotDue ? 'not_yet_due'
        : 'not_filed';
      const mgt7Status = isBS_FY ? 'filed'
        : isBeforeBS ? 'filed'
        : isNotRequired ? 'not_required'
        : mgtNotDue ? 'not_yet_due'
        : 'not_filed';

      rows.push({
        fy: fyLabel,
        aoc4: aoc4Status,
        mgt7: mgt7Status,
        aoc4_verified: isBS_FY,
        mgt7_verified: isBS_FY,
        locked: isBS_FY,
        due_pending: (aocNotDue || mgtNotDue) && !isNotRequired,
        not_required: isNotRequired,
      });
    }
    return rows;
  };

  const [fyRows, setFyRows] = useState(buildDefaultRows);
  const [rulesFlashCount, setRulesFlashCount] = useState(0); // F3: shows "Gap rules updated — N FYs reclassified" pill
  const prevFyRowsRef = useRef(null);

  // F3: On any gap-rule input change, compute how many FY rows were reclassified and flash the pill
  useEffect(() => {
    if (!prevFyRowsRef.current) {
      prevFyRowsRef.current = fyRows;
      return;
    }
    const prev = prevFyRowsRef.current;
    let changed = 0;
    fyRows.forEach(r => {
      const p = prev.find(pr => pr.fy === r.fy);
      if (!p) return;
      if (p.aoc4 !== r.aoc4 || p.mgt7 !== r.mgt7 || p.not_required !== r.not_required || p.due_pending !== r.due_pending) changed++;
    });
    if (changed > 0) {
      setRulesFlashCount(changed);
      const t = setTimeout(() => setRulesFlashCount(0), 5000);
      return () => clearTimeout(t);
    }
    prevFyRowsRef.current = fyRows;
  }, [fyRows]);

  // STK-1 / Sec 248(1) notice toggle state (drives gap-rules engine)
  const storedNoticeStatus = fi.gap_rules_meta?.notice_248_status || '';
  const storedNoticeDate = fi.gap_rules_meta?.notice_date || '';
  const [noticeStatus, setNoticeStatus] = useState(storedNoticeStatus);
  const [noticeDate, setNoticeDate] = useState(storedNoticeDate);

  // Re-sync when lead's gap_rules_meta updates (after saving notice, refresh, etc.)
  useEffect(() => {
    setNoticeStatus(storedNoticeStatus);
    setNoticeDate(storedNoticeDate);
    setFyRows(buildDefaultRows());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedNoticeStatus, storedNoticeDate, lead.cessation_fy, lead.cessation_fy_override]);

  const toggleCell = (fy, field) => {
    setFyRows(prev => prev.map(r => {
      if (r.fy !== fy || r.locked) return r;
      const newVal = r[field] === 'filed' ? 'not_filed' : 'filed';
      const overrides = { ...(r.overrides || {}), [field]: {
        original: r[field], overridden_to: newVal,
        by: 'Admin', at: new Date().toISOString()
      } };
      return { ...r, [field]: newVal, overrides };
    }));
  };

  const toggleVerified = (fy, field) => {
    setFyRows(prev => prev.map(r => {
      if (r.fy !== fy || r.locked) return r;
      return { ...r, [field]: !r[field] };
    }));
  };

  // Deduplicated filing count (same form + same date = 1)
  const dedupFilings = new Set(filings.map(f => `${(f.form_name||'').toUpperCase()}|${f.date_of_filing}`));
  const filingCount = dedupFilings.size;
  // Pending = mandatory gaps only (exclude not_required and not_yet_due)
  const pendingCount = fyRows.filter(r => !r.locked && !r.due_pending && !r.not_required && (!r.aoc4_verified || !r.mgt7_verified) && (r.aoc4 === 'not_filed' || r.mgt7 === 'not_filed')).length;

  const years = [...new Set(filings.map(f => f.date_of_filing?.slice(0, 4)).filter(Boolean))].sort().reverse();
  const filtered = filings.filter(f => {
    const matchSearch = !searchQ || f.form_name?.toLowerCase().includes(searchQ.toLowerCase()) || f.description?.toLowerCase().includes(searchQ.toLowerCase());
    const matchYear = yearFilter === 'ALL' || f.date_of_filing?.startsWith(yearFilter);
    return matchSearch && matchYear;
  });
  const groupedByYear = {};
  filtered.forEach(f => {
    const yr = f.date_of_filing?.slice(0, 4) || 'Unknown';
    if (!groupedByYear[yr]) groupedByYear[yr] = [];
    groupedByYear[yr].push(f);
  });

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api${apiBase}/${lead.id}/filing-index/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setExpanded(true);
        onRefresh?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to parse filing index');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSaveVerification = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api${apiBase}/${lead.id}/filing-index/fy-verification`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fy_rows: fyRows }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Saved — ${data.summary.gaps} gaps, ${data.summary.verified} verified`);
        onRefresh?.();
      } else { toast.error('Failed to save'); }
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleSaveNotice = async (newStatus, newDate) => {
    setSavingNotice(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api${apiBase}/${lead.id}/filing-index/gap-rules`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notice_248_status: newStatus, notice_date: newDate || '' }),
      });
      if (res.ok) {
        toast.success(newStatus === 'yes' ? 'STK-1 marked received — all pending filings now mandatory' : 'STK-1 status updated');
        onRefresh?.();
      } else { toast.error('Failed to update'); }
    } catch { toast.error('Update failed'); }
    finally { setSavingNotice(false); }
  };

  const fmtDate = (d) => fmtDatePretty(d);

  return (
    <div className="bg-white rounded-lg border border-indigo-200 p-4" data-testid={`filing-index-${lead.id}`}>
      <div className="flex items-center justify-between">
        <button onClick={() => filings.length > 0 && setExpanded(!expanded)} className="flex items-center gap-2">
          <p className="text-[10px] text-indigo-600 uppercase tracking-wide font-semibold flex items-center gap-1.5">
            <FileCheck className="w-3.5 h-3.5" /> MCA Filing Index
            {filings.length > 0 && (
              <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold">{filingCount} Filings</span>
            )}
            {pendingCount > 0 && (
              <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-bold">{pendingCount} Pending</span>
            )}
            {rulesFlashCount > 0 && (
              <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold animate-in fade-in duration-300" data-testid={`gap-rules-updated-${lead.id}`}>
                Gap rules updated — {rulesFlashCount} FY reclassified
              </span>
            )}
            {noticeStatus === 'yes' && (
              <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold border border-red-300 flex items-center gap-0.5" data-testid={`stk1-badge-${lead.id}`}>
                <AlertCircle className="w-2.5 h-2.5" /> STK-1 Case
              </span>
            )}
            {lead?.cin_info?.is_section_8 && (
              <span className="ml-1 text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold border border-amber-200 flex items-center gap-0.5" data-testid={`s8-regularise-${lead.id}`} title="Section 8 companies cannot strike off (Sec 248(3)) — all filings till current FY are mandatory. Cessation override does not apply.">
                <AlertCircle className="w-2.5 h-2.5" /> Section 8 · Regularise
              </span>
            )}
          </p>
          {filings.length > 0 && (
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${expanded ? 'bg-indigo-100' : 'bg-slate-100'}`}>
              {expanded ? <ChevronUp className="w-3.5 h-3.5 text-indigo-600" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
            </div>
          )}
        </button>
        {!readOnly && (
        <div className="flex items-center gap-2">
          {lead.documents_folder_url && (
            <a href={lead.documents_folder_url} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full flex items-center justify-center bg-amber-50 hover:bg-amber-100 text-amber-500 hover:text-amber-700 transition-colors" title="Open Document Folder" data-testid={`fi-folder-${lead.id}`}>
              <FolderOpen className="w-3.5 h-3.5" />
            </a>
          )}
          {lead.filing_index_file_path && (
            <Button size="sm" variant="ghost" onClick={async () => {
              const token = localStorage.getItem('ratnatraya_token');
              const res = await fetch(`${API}/api${apiBase}/${lead.id}/filing-index/download`, { headers: { Authorization: `Bearer ${token}` } });
              if (res.ok) { const blob = await res.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = lead.filing_index_file_name || 'Filing_Index.xlsx'; a.click(); }
            }} className="h-7 text-xs gap-1 text-slate-500 hover:text-indigo-700" data-testid={`download-fi-${lead.id}`}>
              <Download className="w-3 h-3" /> File
            </Button>
          )}
          <input type="file" ref={fileRef} accept=".xlsx,.xls" className="hidden" onChange={e => handleUpload(e.target.files[0])} />
          {filings.length > 0 ? (
            <span className="h-7 text-[10px] px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-400 flex items-center gap-1" title="Filing Index is locked once parsed. Delete lead to re-upload." data-testid={`filing-locked-${lead.id}`}>
              <Lock className="w-3 h-3" /> Locked
            </span>
          ) : (
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-7 text-xs gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid={`upload-filing-index-${lead.id}`}>
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Upload Filing Index
            </Button>
          )}
        </div>
        )}
      </div>

      {/* Auto-detected summary */}
      {filings.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {fi.adt1_last_filed && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">ADT-1: {fmtDate(fi.adt1_last_filed)}</span>
          )}
          {fi.has_stk2 === false && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">No STK-2</span>
          )}
          {fi.has_stk2 === true && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">STK-2 filed!</span>
          )}
          {fi.has_msc1 === false && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">No MSC-1</span>
          )}
        </div>
      )}
      {fi.uploaded_at && (
        <p className="text-[10px] text-slate-400 mt-1">Uploaded: {new Date(fi.uploaded_at).toLocaleString('en-IN')}{fi.uploaded_by ? ` by ${fi.uploaded_by}` : ''}</p>
      )}

      {/* Expanded: FY Verification Table + Filing Search */}
      {expanded && filings.length > 0 && (
        <div className="mt-3 space-y-3 animate-in fade-in duration-200">
          {/* STK-1 / Sec 248(1) Notice toggle — only for close/strike-off intent */}
          {(() => {
            const intentVal = (lead.quiz_answers?.[2] || lead.gap_rules_meta?.intent || lead.intent || '').toString().toLowerCase().trim();
            const isCloseIntent = intentVal === 'close' || intentVal === 'strike_off';
            if (!isCloseIntent) return null;
            return (
              <div className={`border rounded-lg p-3 ${noticeStatus === 'yes' ? 'border-red-300 bg-red-50/40' : 'border-slate-200 bg-slate-50/40'}`} data-testid={`stk1-notice-card-${lead.id}`}>
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className={`w-4 h-4 mt-0.5 shrink-0 ${noticeStatus === 'yes' ? 'text-red-600' : 'text-slate-500'}`} />
                  <div className="flex-1">
                    <p className="text-[11px] font-bold text-slate-800 uppercase tracking-wide">ROC Notice — Sec 248(1) / STK-1</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Manual check — MCA portal may still show company as active even after STK-1 is served.</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 pl-6">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name={`stk1-${lead.id}`} checked={noticeStatus !== 'yes'} disabled={readOnly || savingNotice}
                      onChange={() => { setNoticeStatus('no'); setNoticeDate(''); handleSaveNotice('no', ''); }}
                      data-testid={`stk1-not-received-${lead.id}`} />
                    <span className="text-xs text-slate-700">Not received</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name={`stk1-${lead.id}`} checked={noticeStatus === 'yes'} disabled={readOnly || savingNotice}
                      onChange={() => setNoticeStatus('yes')}
                      data-testid={`stk1-received-${lead.id}`} />
                    <span className="text-xs text-slate-700 font-semibold">Received on:</span>
                  </label>
                  <input type="date" value={noticeDate || ''} disabled={noticeStatus !== 'yes' || readOnly || savingNotice}
                    onChange={e => setNoticeDate(e.target.value)}
                    onBlur={() => noticeStatus === 'yes' && handleSaveNotice('yes', noticeDate)}
                    className="text-xs px-2 py-1 rounded border border-slate-300 bg-white disabled:bg-slate-100 disabled:text-slate-400"
                    data-testid={`stk1-date-${lead.id}`} />
                  {savingNotice && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                </div>
                {noticeStatus === 'yes' && (
                  <div className="mt-2 pl-6 flex items-start gap-1.5 text-[10px] text-red-700 font-medium">
                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>STK-1 received{noticeDate ? ` on ${fmtDate(noticeDate)}` : ''} — all pending filings till latest verifiable FY must be completed before responding to ROC.</span>
                  </div>
                )}
                {noticeStatus !== 'yes' && (
                  <p className="mt-1.5 pl-6 text-[10px] text-slate-500">Default: post-cessation FYs are treated as &quot;Not Required&quot; for strike-off.</p>
                )}
              </div>
            );
          })()}
          {/* Side-by-side: FY Verification (left) + Filing Search (right) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* LEFT: FY Verification Table */}
            {fyRows.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`fy-summary-${lead.id}`}>
                <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200">
                  <p className="text-[10px] text-slate-600 uppercase font-bold tracking-wider">FY-wise Filing Verification</p>
                  {!readOnly && (
                  <Button size="sm" variant="outline" onClick={handleSaveVerification} disabled={saving}
                    className="h-6 text-[10px] gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid={`save-fy-verification-${lead.id}`}>
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                  </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-[10px]">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5 text-slate-500 font-semibold w-16">FY</th>
                        <th className="text-center px-1 py-1.5 text-slate-500 font-semibold">AOC-4</th>
                        <th className="text-center px-1 py-1.5 text-slate-500 font-semibold w-6" title="AOC-4 verified">✓</th>
                        <th className="text-center px-1 py-1.5 text-slate-500 font-semibold">MGT-7</th>
                        <th className="text-center px-1 py-1.5 text-slate-500 font-semibold w-6" title="MGT-7 verified">✓</th>
                        <th className="text-center px-1 py-1.5 text-slate-500 font-semibold">Other Key Filings</th>
                        <th className="text-center px-1 py-1.5 text-slate-500 font-semibold w-16">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {fyRows.map(r => {
                        const bothVerified = r.aoc4_verified && r.mgt7_verified;
                        const hasGap = r.aoc4 === 'not_filed' || r.mgt7 === 'not_filed';
                        const rowStatus = r.not_required ? 'not_required' : r.locked ? 'confirmed' : r.due_pending ? 'due_pending' : bothVerified ? (hasGap ? 'gap' : 'verified') : 'pending';
                        return (
                          <tr key={r.fy} className={`${
                            r.not_required ? 'bg-slate-50/40 opacity-60' :
                            r.locked ? 'bg-blue-50/60' :
                            r.due_pending ? 'bg-slate-50/60' :
                            rowStatus === 'verified' ? 'bg-emerald-50/40' :
                            rowStatus === 'gap' ? 'bg-red-50/40' :
                            'bg-white'
                          }`} data-testid={`fy-row-${r.fy}`}>
                            <td className="px-2 py-1.5 font-semibold text-slate-700">{r.fy}</td>
                            <td className="text-center px-1 py-1.5">
                              {r.not_required ? (
                                <span className="text-slate-400 text-[9px]">Not Required</span>
                              ) : r.locked ? (
                                <span className="text-blue-700 font-medium">Filed</span>
                              ) : r.due_pending ? (
                                <span className="text-slate-400 text-[9px]">Not Yet Due</span>
                              ) : readOnly ? (
                                <span className={`px-2 py-0.5 rounded text-[9px] font-medium ${r.aoc4 === 'filed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{r.aoc4 === 'filed' ? 'Filed' : 'Not Filed'}</span>
                              ) : (
                                <button onClick={() => toggleCell(r.fy, 'aoc4')}
                                  className={`px-2 py-0.5 rounded text-[9px] font-medium transition-colors cursor-pointer ${
                                    r.aoc4 === 'filed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-red-100 text-red-600 hover:bg-red-200'
                                  }`} data-testid={`aoc4-toggle-${r.fy}`}>
                                  {r.aoc4 === 'filed' ? 'Filed' : 'Not Filed'}
                                </button>
                              )}
                              {r.overrides?.aoc4 && (
                                <span className="ml-1 text-[8px] text-amber-600" title={`Overridden from ${r.overrides.aoc4.original} by ${r.overrides.aoc4.by}`}>✏</span>
                              )}
                            </td>
                            <td className="text-center px-1 py-1.5">
                              {r.not_required ? (
                                <span className="text-slate-300">—</span>
                              ) : r.locked ? (
                                <span className="text-blue-500 text-xs">✓</span>
                              ) : r.due_pending ? (
                                <span className="text-slate-300">—</span>
                              ) : (
                                <input type="checkbox" checked={!!r.aoc4_verified} onChange={() => !readOnly && toggleVerified(r.fy, 'aoc4_verified')} disabled={readOnly}
                                  className="w-3 h-3 rounded border-slate-300 text-indigo-600 cursor-pointer" data-testid={`aoc4-verify-${r.fy}`} />
                              )}
                            </td>
                            <td className="text-center px-1 py-1.5">
                              {r.not_required ? (
                                <span className="text-slate-400 text-[9px]">Not Required</span>
                              ) : r.locked ? (
                                <span className="text-blue-700 font-medium">Filed</span>
                              ) : r.due_pending ? (
                                <span className="text-slate-400 text-[9px]">Not Yet Due</span>
                              ) : readOnly ? (
                                <span className={`px-2 py-0.5 rounded text-[9px] font-medium ${r.mgt7 === 'filed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{r.mgt7 === 'filed' ? 'Filed' : 'Not Filed'}</span>
                              ) : (
                                <button onClick={() => toggleCell(r.fy, 'mgt7')}
                                  className={`px-2 py-0.5 rounded text-[9px] font-medium transition-colors cursor-pointer ${
                                    r.mgt7 === 'filed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-red-100 text-red-600 hover:bg-red-200'
                                  }`} data-testid={`mgt7-toggle-${r.fy}`}>
                                  {r.mgt7 === 'filed' ? 'Filed' : 'Not Filed'}
                                </button>
                              )}
                              {r.overrides?.mgt7 && (
                                <span className="ml-1 text-[8px] text-amber-600" title={`Overridden from ${r.overrides.mgt7.original} by ${r.overrides.mgt7.by}`}>✏</span>
                              )}
                            </td>
                            <td className="text-center px-1 py-1.5">
                              {r.not_required ? (
                                <span className="text-slate-300">—</span>
                              ) : r.locked ? (
                                <span className="text-blue-500 text-xs">✓</span>
                              ) : r.due_pending ? (
                                <span className="text-slate-300">—</span>
                              ) : (
                                <input type="checkbox" checked={!!r.mgt7_verified} onChange={() => !readOnly && toggleVerified(r.fy, 'mgt7_verified')} disabled={readOnly}
                                  className="w-3 h-3 rounded border-slate-300 text-indigo-600 cursor-pointer" data-testid={`mgt7-verify-${r.fy}`} />
                              )}
                            </td>
                            <td className="text-center px-1 py-1.5">
                              {(() => {
                                const okFilings = otherKeyByFY[r.fy] || [];
                                if (okFilings.length > 0) {
                                  return (
                                    <div className="flex flex-wrap gap-0.5 justify-center">
                                      {okFilings.map((f) => {
                                        const colors = f.form.includes('ADT') ? 'bg-emerald-100 text-emerald-700' : f.form.includes('PAS') || f.form.includes('SH') ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700';
                                        return <span key={`${f.form}-${f.date}`} className={`text-[8px] px-1 py-0.5 rounded font-medium ${colors}`} title={f.date}>{f.form} <span className="opacity-60">{fmtDate(f.date)}</span></span>;
                                      })}
                                    </div>
                                  );
                                }
                                return <span className="text-slate-300">—</span>;
                              })()}
                            </td>
                            <td className="text-center px-1 py-1.5">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                                rowStatus === 'not_required' ? 'bg-slate-100 text-slate-400' :
                                rowStatus === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                rowStatus === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                                rowStatus === 'gap' ? 'bg-red-100 text-red-600' :
                                rowStatus === 'due_pending' ? 'bg-slate-100 text-slate-400' :
                                'bg-slate-100 text-slate-500'
                              }`}>
                                {rowStatus === 'not_required' ? 'Not Required' : rowStatus === 'confirmed' ? 'MCA' : rowStatus === 'verified' ? 'Verified' : rowStatus === 'gap' ? 'Gap' : rowStatus === 'due_pending' ? 'Not Yet Due' : 'Pending'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* RIGHT: Filing Search + List */}
            <div className={`border border-slate-200 rounded-lg overflow-hidden ${fyRows.length === 0 ? 'lg:col-span-2' : ''}`}>
              <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center gap-2">
                <input type="text" placeholder="Search form or description..."
                  value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  className="flex-1 text-xs px-2.5 py-1.5 rounded border border-slate-200 bg-white max-w-xs"
                  data-testid={`filing-search-${lead.id}`} />
                <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
                  className="text-xs px-2 py-1.5 rounded border border-slate-200 bg-white"
                  data-testid={`filing-year-filter-${lead.id}`}>
                  <option value="ALL">All Years</option>
                  {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
                </select>
                <span className="text-[10px] text-slate-400">{filtered.length} shown</span>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {Object.keys(groupedByYear).sort().reverse().map(yr => (
                  <div key={yr}>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100 px-3 py-1 bg-slate-50/50">{yr}</p>
                    {groupedByYear[yr].map((f, i) => (
                      <div key={`${f.form_name}-${f.date_of_filing}-${i}`} className="flex items-start gap-3 text-xs py-1.5 px-3 hover:bg-slate-50">
                        <span className="text-slate-400 shrink-0 w-16 tabular-nums">{fmtDate(f.date_of_filing)}</span>
                        <span className="font-semibold text-indigo-700 shrink-0 w-24">{f.form_name}</span>
                        <span className="text-slate-600 flex-1 truncate">{f.description || '—'}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
